import { useState, useEffect, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DeliveryFormValues } from "../lib/schemas/travel-booking";
import { deliverySchema } from "../lib/schemas/travel-booking";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LinkedSelect as ReactSelect } from "../ui-form/linked-controls";
import { Option } from "components/Form/form";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormField } from "../ui-form/form-field";
import { useQuery } from "react-query";
import { getAddressById } from "../apis";
import { listServiceableCities } from "service/moneeflo/legacy-forex-create-api";
import { useAppSelector } from "redux/hooks";
// Same signature, answered by the new backend. See legacy-lookup-api.ts.
import { getRelationShipManager } from "service/moneeflo/legacy-lookup-api";
import { useSelectState } from "pages/Opportunities/utils/useSelect";

interface DeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (delivery: DeliveryFormValues) => void;
  initialData?: DeliveryFormValues;
  productOptions: any[];
  customers: any[];
  statusInfo?: { id: string; status: string; date: string; assign_to: string };
  showStatusSection?: boolean;
  branch?: any;
}

// Mock data
const deliveryModes = [
  { value: "self", label: "Delivery" },
  { value: "courier", label: "Courier" },
  { value: "pickup", label: "Pickup" },
];

const countries = [
  { value: "us", label: "United States" },
  { value: "ca", label: "Canada" },
  { value: "uk", label: "United Kingdom" },
  { value: "in", label: "India" },
];

const deliveryExecutives = [
  { id: "65", name: "Shivam Rajput - Accountant + Cashier" },
  { id: "52", name: "Dheeraj Singh - Operation Executive" },
  { id: "30", name: "Monika - Operation Executive" },
  { id: "73", name: "Vipan Kumar Sharma - Executive Customer Service" },
  { id: "74", name: "Dalip Gupta - Executive Customer Service" },
  { id: "48", name: "Basant Kumar Sharma - Executive Customer Service" },
  { id: "72", name: "Pawan Kumar Pandey - Executive Customer Service" },
];

// Mock API call to get states by country
const fetchStatesByCountry = async (countryCode: string) => {
  // Simulate API call
  await new Promise((resolve) => setTimeout(resolve, 300));

  // Return mock states based on country
  const statesByCountry: Record<string, any[]> = {
    us: [
      { value: "ny", label: "New York" },
      { value: "ca", label: "California" },
      { value: "tx", label: "Texas" },
    ],
    ca: [
      { value: "on", label: "Ontario" },
      { value: "qc", label: "Quebec" },
      { value: "bc", label: "British Columbia" },
    ],
    uk: [
      { value: "eng", label: "England" },
      { value: "sct", label: "Scotland" },
      { value: "wls", label: "Wales" },
    ],
    in: [
      { value: "mh", label: "Maharashtra" },
      { value: "dl", label: "Delhi" },
      { value: "ka", label: "Karnataka" },
    ],
  };

  return statesByCountry[countryCode] || [];
};

const defaultDelivery: DeliveryFormValues = {
  deliveryMode: { value: "", label: "" },
  deliveryId: "",
  trackingId: "",
  products: [],
  customers: { value: "", label: "" },
  deliveryType: "self",
  locationInfo: "",
  addresses: [
    {
      address1: "",
      address2: "",
      landmark: "",
      country: { value: "in", label: "India" },
      state: { value: "", label: "" },
      city: "",
      pincode: "",
    },
  ],
  address_id: "",
  local_id: [],
  date: ""
};

export function DeliveryModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  productOptions,
  customers,
  statusInfo,
  showStatusSection = true, branch
}: DeliveryModalProps) {
  const [, setStateOptions] = useState<any[]>([]);
  const { newGenricOrderID } = useAppSelector((state) => state.CreateOrder);
  // Create a form specifically for this modal
  const form = useForm<DeliveryFormValues>({
    resolver: zodResolver(deliverySchema),
    defaultValues: defaultDelivery,
    mode: "onChange",
  });


  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!initialData?.customers || !customers?.length) return;

    const matchedCustomer = customers.find(
      (c) =>
        String(c.customerId ?? c.value) ===
        String(initialData.customers.value)
    );

    if (matchedCustomer) {
      form.setValue(
        "customers",
        {
          value: String(matchedCustomer.customerId ?? matchedCustomer.value),
          label: matchedCustomer.fullName ?? matchedCustomer.label,
        },
        { shouldDirty: false }
      );
    }
  }, [initialData, customers, form]);

  useEffect(() => {
    // EDIT MODE
    if (initialData?.products?.length) {
      form.setValue("products", initialData.products, {
        shouldValidate: true,
        shouldDirty: false,
      });
      return;
    }

    // CREATE MODE
    if (productOptions?.length) {
      form.setValue("products", productOptions, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [initialData, productOptions, form]);



  useEffect(() => {
    const addressErrors = form?.formState?.errors?.addresses;

    if (Array.isArray(addressErrors)) {
      const hasAnyAddressError = addressErrors.some((addrErr) =>
        addrErr && Object.keys(addrErr).length > 0
      );

      if (hasAnyAddressError) {
        setIsFormOpen(true);
      }
    }
  }, [form?.formState?.errors?.addresses]);
console.log("error", form.formState.errors);
  // default Mode
  const defaultDeliveryMode = form?.formState?.defaultValues?.deliveryMode;
  // default Date get 
  const defaultDeliveryDate = form?.formState?.defaultValues?.date;


  const deliveryType = form.watch("deliveryType");
  const addresses = form.watch("addresses") || [];
  const [statusObj, setStatusObj] = useState({
    status: statusInfo?.status || "",
    date: statusInfo?.date || "",
    assign_to: statusInfo?.assign_to || "",
    quantity: statusInfo?.quantity || 0,
  });

  const handleDeliveryTypeChange = (value: "self" | "courier" | "pickup") => {
    form.setValue("deliveryType", value, { shouldValidate: true });
  };

  const handleAddressChange = async (
    index: number,
    field: string,
    value: any
  ) => {
    const updatedAddresses = [...addresses];
    updatedAddresses[index] = { ...updatedAddresses[index], [field]: value };
    form.setValue("addresses", updatedAddresses, { shouldValidate: true });

    // Clear address_id if the user is manually editing fields
    if (field !== "country" && field !== "state") {
      form.setValue("address_id", "");
    }

    // If country changes, fetch states
    if (field === "country" && value?.value) {
      const states = await fetchStatesByCountry(value.value);
      setStateOptions(states);
    }
  };


  console.log(form.formState.errors);

  const addAddress = () => {
    form.setValue(
      "addresses",
      [
        ...addresses,
        {
          address1: "",
          address2: "",
          landmark: "",
          country: {
            value: "",
            label: "",
          },
          state: {
            value: "",
            label: "",
          },
          city: "",
          pincode: "",
        },
      ],
      { shouldValidate: true }
    );
  };

  const removeAddress = (index: number) => {
    const updatedAddresses = addresses.filter((_, i) => i !== index);
    form.setValue("addresses", updatedAddresses, { shouldValidate: true });
  };

  const handleSubmit = (values: DeliveryFormValues) => {
    console.log("✅ SUBMITTING DELIVERY", values);

    if (initialData) {
      values.statusData = statusObj;
    }

    onSubmit(values); // API call
    onClose();        // close modal AFTER submit
  };


  const filteredCustomers = customers?.map((customer) => ({
    value: customer?.customerId ?? customer?.value,
    label: customer?.fullName ?? customer?.label,
  }));


  // The customer's id is core's uuid, not a number.
  const customerId = String(form.watch("customers")?.value ?? "");
  const {
    data: queryAddresses,
  } = useQuery({
    queryKey: ["addressByCustomerId", customerId],
    queryFn: () => getAddressById({ customers_id: customerId }),
    enabled: customerId !== "",
  });
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  // The cities we deliver to. A saved address's typed city is matched to one by
  // name, so the picker starts on it when it is on the list.
  const { data: cities } = useQuery({ queryKey: ["deliveryCities"], queryFn: listServiceableCities, staleTime: 5 * 60 * 1000 });
  const typedCity = form.watch("addresses.0.city");
  const pickedCityId = form.watch("addresses.0.cityId");
  useEffect(() => {
    if (!cities) return;
    const current = cities.find((c) => c.id === pickedCityId);
    if (current && current.name.toLowerCase() === String(typedCity ?? "").trim().toLowerCase()) return;
    const byName = cities.find((c) => c.name.toLowerCase() === String(typedCity ?? "").trim().toLowerCase());
    form.setValue("addresses.0.cityId", byName?.id ?? "");
  }, [cities, typedCity]);

  const handleAddClick = () => {
    setIsFormOpen((prev) => !prev);
  };

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "customers" && value.customers) {
        const hasAddresses =
          (Array.isArray(queryAddresses) && queryAddresses.length > 0) ||
          queryAddresses?.addresses?.length > 0 ||
          queryAddresses?.address_id!?.length > 0;

        setIsFormOpen(!hasAddresses);
      }
    });

    return () => subscription.unsubscribe();
  }, [form, queryAddresses]);
console.log("initialData", initialData);
  useEffect(() => {
    console.log("initialData?.address_id", initialData?.address_id);
    console.log("queryAddresses", queryAddresses);
    if (initialData?.address_id && queryAddresses?.address_id && queryAddresses.address_id.length > 0) {
      const address = queryAddresses.address_id.find(
        (addr: { id: string }) => addr.id === initialData.address_id
      );

      console.log("address_info", address);
      if (address) {
        form.setValue("addresses.0.address1", address?.house_number || "", { shouldDirty: true });
        form.setValue("addresses.0.address2", address?.street_number || "", { shouldDirty: true });
        form.setValue("addresses.0.city", address?.City || "", { shouldDirty: true });
        form.setValue("addresses.0.state", {
          value: address?.State || "",
          label: address?.State || "",
        }, { shouldDirty: true });
        form.setValue("addresses.0.pincode", address?.pincode || "", { shouldDirty: true });
        form.setValue("addresses.0.landmark", address?.landmark || "", { shouldDirty: true });
        form.setValue("addresses.0.country", {
          value: address?.country_code || "in",
          label: address?.country_name || "India",
        }, { shouldDirty: true });

        // Fetch states if country code is available
        if (address?.country_code) {
          fetchStatesByCountry(address.country_code).then((states) => {
            setStateOptions(states);
          });
        }
      }
    }
  }, [initialData?.address_id, queryAddresses, form]);

  const handleEditAddress = async (address: any, index: number) => {
    form.setValue("address_id", address?.id?.toString() || index.toString());
    form.setValue("addresses.0.address1", address?.house_number || "");
    form.setValue("addresses.0.address2", address?.street_number || "");
    form.setValue("addresses.0.city", address?.City || "");
    form.setValue("addresses.0.state", {
      value: address?.State || "",
      label: address?.State || "",
    });
    form.setValue("addresses.0.pincode", address?.pincode || "");
    form.setValue("addresses.0.landmark", address?.landmark || "");

    if (address?.country_code) {
      const states = await fetchStatesByCountry(address.country_code);
      setStateOptions(states);
    }
    handleAddClick();
  };
  console.log("defaultDeliveryMode", form.getValues("deliveryMode.value"));
  
      const {
          options: relationShipManagerOptions,
          selectedOption: assignedToOption,
          handleChange: handleAssignedToChange,
      } = useSelectState(
          [],
          null,
          () => { },
          () => { },
          fetchRelationShipManager as () => Promise<Option[]>,
      );
  
      const {
          options: ownerOptions,
          selectedOption: ownedByOption,
          handleChange: handleOwnedByChange,
      } = useSelectState(
          [],
          null,
          () => { },
          () => { },
          fetchRelationShipManager as () => Promise<Option[]>,
      );
  
      async function fetchRelationShipManager(): Promise<void | Option[]> {
          // get auth token from localstorage
          const auth: string | null = localStorage.getItem("authToken");
          if (auth === null) return;
          const authToken = JSON.parse(auth);
  
          const req = {
              headers: {
                  Authorization: `Bearer ${authToken}`,
              },
          };
          const name = localStorage.getItem("name");
          const is_owner = localStorage.getItem("is_owner");
          const owner_name = localStorage.getItem("owner_name");
          const myself = localStorage.getItem("staff_id");
  
          try {
              const res = await getRelationShipManager(req);
  
              const options = res.data.map((obj) => ({
                  label: obj?.fname,
                  value: obj?.id,
              }));
  
              const index = options.findIndex((item) => item?.value == myself);
              if (index > 0) {
                  const item = options.splice(index, 1)[0];
                  options.unshift(item);
              }
  
              return options as Option[];
          } catch (error) {
              console.error("Error fetching options staff");
          }
      }
  
  return (
    <AlertDialog open={isOpen} >
      <AlertDialogPortal>
        <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
        <AlertDialogContent aria-describedby={undefined} className="wizard-dialog max-w-4xl p-0 rounded-lg bg-white-A700">
          <AlertDialogTitle className="sr-only">{initialData ? "Edit delivery" : "Add delivery"}</AlertDialogTitle>
          <AlertDialogHeader className="flex text-left gap-4 py-6 px-10 border-b">
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold text-gray-900">
                {initialData ? "Edit Delivery" : "Add Delivery"}
              </h2>
              <p className="text-sm text-left text-[#555555] mt-1">
                Fill the required details
              </p>
            </div>
          </AlertDialogHeader>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(
              (values) => {
                console.log("🔥 RHF handleSubmit STARTED");
                handleSubmit(values);
              },
              (errors) => {
                console.error("❌ SUBMIT BLOCKED - FORM ERRORS", errors);
              }
            )}>
              <div data-testid="delivery-modal-body" className="space-y-4 px-10 pb-5 pt-1 overflow-auto max-h-[min(500px,62vh)]">
                <div className="grid z-10 grid-cols-2 gap-5 pr-[38px]">
                  <FormField
                    form={form}
                    name="deliveryMode"
                    label="Delivery Mode"
                    render={({ field }) => (
                      <ReactSelect
                        options={deliveryModes}
                        value={field.value}
                        onChange={(option) => {
                          form.setValue("deliveryMode", option as Option, {
                            shouldValidate: true,
                          });

                          const deliveryType = (
                            option?.value || ""
                          ).toLowerCase();
                          form.setValue(
                            "deliveryType",
                            deliveryType as "self" | "courier" | "pickup",
                            {
                              shouldValidate: true,
                            }
                          );
                        }}
                        placeholder="Select delivery mode"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        components={{ IndicatorSeparator: () => null }}
                        // isDisabled={
                        //   !!defaultDeliveryMode?.value &&
                        //   defaultDeliveryMode.value !== ""
                        // }
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            padding: "8px",
                            borderRadius: "6px",
                            borderColor: state.isFocused
                              ? "#3b82f6"
                              : base.borderColor,
                            boxShadow: state.isFocused
                              ? "0 0 0 1px #3b82f6"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: "#3b82f6",
                            },
                            backgroundColor:
                              !!defaultDeliveryMode?.value &&
                                defaultDeliveryMode.value !== ""
                                ? "#f9fafb"
                                : base.backgroundColor,
                            cursor:
                              !!defaultDeliveryMode?.value &&
                                defaultDeliveryMode.value !== ""
                                ? "not-allowed"
                                : "default",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: "0 8px",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "14px",
                            fontWeight: 500,
                          }),
                        }}
                      />
                    )}
                  />
                  {form.getValues("deliveryMode.value") === "courier" && (
                    <FormField
                      form={form}
                      name="trackingId"
                      label="Tracking ID"
                      render={({ field }) => (
                        <input
                          {...field}
                          disabled={
                            initialData &&
                            form.getValues("deliveryMode.value") !== "courier"
                          }
                          placeholder="Enter Tracking ID"
                          className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                        />
                      )}
                    />
                  )}
                  <FormField
                    form={form}
                    name="customers"
                    label="Customers"
                    render={({ field }) => (
                      <ReactSelect
                        options={filteredCustomers}
                        value={field.value}
                        isDisabled={!!initialData}
                        onChange={(option) => {
                          field.onChange(option);
                        }}
                        placeholder="Select customers"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        components={{ IndicatorSeparator: () => null }}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            padding: "8px",
                            borderRadius: "6px",
                            borderColor: state.isFocused
                              ? "#3b82f6"
                              : base.borderColor,
                            boxShadow: state.isFocused
                              ? "0 0 0 1px #3b82f6"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: "#3b82f6",
                            },
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: "0 8px",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "14px",
                            fontWeight: 500,
                          }),
                        }}
                      />
                    )}
                  />
                </div>
                {true && (
                  <div>
                    {form.getValues("deliveryMode.value") !== "pickup" ? (
                      queryAddresses?.address_id && (
                        <div className="mt-6">
                          <h3 className="text-md font-medium mb-3">
                            Saved addresses
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {queryAddresses?.address_id?.map(
                              (address: any, index: number) => {
                                const selectedId = form.getValues("address_id");
                                const currentId =
                                  address?.id?.toString() || index.toString();
                                const isSelected = selectedId === currentId;
                                return (
                                  <button
                                    key={index}
                                    type="button"
                                    onClick={() => {
                                      if (isSelected) {
                                        form.setValue("address_id", "");
                                        form.setValue("addresses.0.address1", "");
                                        form.setValue("addresses.0.address2", "");
                                        form.setValue("addresses.0.city", "");
                                        form.setValue("addresses.0.state", {
                                          value: "",
                                          label: "",
                                        });
                                        form.setValue("addresses.0.pincode", "");
                                        form.setValue("addresses.0.landmark", "");
                                        return;
                                      }

                                      form.setValue("address_id", currentId, {
                                        shouldDirty: true,
                                      });
                                      form.setValue(
                                        "addresses.0.address1",
                                        address?.house_number || "",
                                        { shouldDirty: true }
                                      );
                                      form.setValue(
                                        "addresses.0.address2",
                                        address?.street_number || "",
                                        { shouldDirty: true }
                                      );
                                      form.setValue(
                                        "addresses.0.city",
                                        address?.City || "",
                                        { shouldDirty: true }
                                      );
                                      form.setValue(
                                        "addresses.0.state",
                                        {
                                          value: address?.State || "",
                                          label: address?.State || "",
                                        },
                                        { shouldDirty: true }
                                      );
                                      form.setValue(
                                        "addresses.0.pincode",
                                        address?.pincode || "",
                                        { shouldDirty: true }
                                      );
                                      form.setValue(
                                        "addresses.0.landmark",
                                        address?.landmark || "",
                                        { shouldDirty: true }
                                      );
                                    }}
                                    className={`w-full text-left ${isSelected
                                      ? "border border-blue-700 rounded-lg"
                                      : ""
                                      }`}
                                  >
                                    <div className="bg-[#F3F7FF] min-h-[160px] rounded-xl px-5 py-3 space-y-1.5 transition-colors relative">
                                      {/* Edit icon only when selected */}
                                      {isSelected && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditAddress(address, index);
                                          }}
                                          className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100 flex items-center"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5 text-[#555555]"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                          >
                                            <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                                            <path
                                              fillRule="evenodd"
                                              d="M2 5a2 2 0 012-2h4a1 1 0 110 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V5z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </button>
                                      )}

                                      <h2 className="text-[#2D3648] font-medium leading-4 -tracking-[1%]">
                                        {form.getValues("customers")?.label}
                                      </h2>

                                      <div className="flex flex-col gap-1">
                                        <h3 className="text-black-label font-normal leading-4 -tracking-[1%]">
                                          {address?.house_number +
                                            " " +
                                            address?.street_number}
                                        </h3>
                                        <p className="text-[#666666] font-normal leading-4 -tracking-[1%]">
                                          {address?.City +
                                            ", " +
                                            address?.State +
                                            " " +
                                            address?.pincode}
                                        </p>
                                        {address?.landmark && (
                                          <p className="text-[#666666] text-sm font-normal leading-4 -tracking-[1%]">
                                            Landmark: {address?.landmark}
                                          </p>
                                        )}
                                      </div>

                                      {isSelected && (
                                        <div className="!mt-4 flex items-center">
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="mr-1"
                                          >
                                            <path
                                              d="M8 0C3.584 0 0 3.584 0 8C0 12.416 3.584 16 8 16C12.416 16 16 12.416 16 8C16 3.584 12.416 0 8 0ZM6.4 12L2.4 8L3.528 6.872L6.4 9.736L12.472 3.664L13.6 4.8L6.4 12Z"
                                              fill="#165FE3"
                                            />
                                          </svg>
                                          <span className="text-blue-700 text-sm font-medium">
                                            Selected
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                );
                              }
                            )}
                          </div>
                        </div>
                      )) : (branch && (
                        <div className="flex flex-1 ">
                          <div className="flex items-start flex-col  flex-wrap"><p>Branch Address: </p>
                        <div className="bg-[#F3F7FF] mt-2 min-h-[160px] rounded-xl px-5 py-3 space-y-1.5 transition-colors relative">

                         
                            <div>
                              {branch.address_line_1}, {branch.address_line_2}, {branch.city}, {branch.state} - {branch.pincode}
                            </div>

                          </div>
                          </div></div>
                      ))}<br></br>
                  </div>
                     ) }
                <div className="space-y-4 ">
                  {form.getValues("deliveryMode.value") !== "pickup" && (
                  <div className="flex justify-between items-center mt-10">
                    <FormLabel className="text-[#666666]">
                      Delivery Address*
                    </FormLabel>
                    <button
                      className="text-[#165FE3] text-[14px] font-semibold"
                      onClick={handleAddClick}
                      type="button"
                    >
                      Add
                    </button>
                    </div>)}

                  {form.getValues("deliveryMode.value") !== "pickup" && isFormOpen &&
                    addresses?.map((address, index) => (
                      <div
                        key={index}
                        className="border-2 border-dotted border-gray-400 rounded-md p-4 space-y-3"
                      >
                        <div className="flex justify-between items-center">
                          {/* <h4 className="text-sm font-medium">
                          Address {index + 1}
                        </h4> */}
                          {index > 0 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAddress(index)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-5 text-[#2D3648]">
                            <FormField
                              form={form}
                              name={`addresses.${index}.address1` as any}
                              label="Address Line 1"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Address line 1"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                            <FormField
                              form={form}
                              name={`addresses.${index}.address2` as any}
                              label="Address Line 2"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Address line 2"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                            <FormField
                              form={form}
                              name={`addresses.${index}.landmark` as any}
                              label="Landmark"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Landmark"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <FormField
                              form={form}
                              name={`addresses.${index}.country` as any}
                              label="Country"
                              render={({ field }) => (
                                <ReactSelect
                                  options={countries}
                                  isDisabled
                                  value={
                                    field.value
                                      ? countries.find(
                                        (option) =>
                                          option.value === field.value.value
                                      )
                                      : null
                                  }
                                  onChange={(option) =>
                                    handleAddressChange(
                                      index,
                                      "country",
                                      option
                                    )
                                  }
                                  placeholder="Select country"
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  components={{
                                    IndicatorSeparator: () => null,
                                  }}
                                  styles={{
                                    control: (base, state) => ({
                                      ...base,
                                      padding: "8px",
                                      borderRadius: "6px",
                                      borderColor: state.isFocused
                                        ? "#3b82f6"
                                        : base.borderColor,
                                      boxShadow: state.isFocused
                                        ? "0 0 0 1px #3b82f6"
                                        : base.boxShadow,
                                      "&:hover": {
                                        borderColor: "#3b82f6",
                                      },
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: "0 8px",
                                    }),
                                    singleValue: (base) => ({
                                      ...base,
                                      fontSize: "14px",
                                      fontWeight: 500,
                                    }),
                                  }}
                                />
                              )}
                            />
                            <FormField
                              form={form}
                              name={`addresses.${index}.state` as any}
                              label="State"
                              render={({ field }) => (
                                // <Select
                                //   options={stateOptions}
                                //   value={
                                //     field.value
                                //       ? stateOptions.find((option) => option.value === field.value.value)
                                //       : null
                                //   }
                                //   onChange={(option) => handleAddressChange(index, "state", option)}
                                //   placeholder="Select state"
                                //   className="react-select-container"
                                //   classNamePrefix="react-select"
                                //   isDisabled={!address.country}
                                // />
                                <input
                                  {...field}
                                  value={field.value.value}
                                  onChange={(e) =>
                                    field.onChange({
                                      label: "",
                                      value: e.target.value,
                                    })
                                  }
                                  placeholder="State"
                                  
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <FormField
                              form={form}
                              name={`addresses.${index}.cityId` as any}
                              label="City"
                              render={({ field }) => (
                                <select
                                  aria-label="City"
                                  value={String(field.value ?? "")}
                                  onChange={(e) => {
                                    const picked = (cities ?? []).find((c) => c.id === e.target.value);
                                    field.onChange(e.target.value);
                                    form.setValue(`addresses.${index}.city` as any, picked?.name ?? "", { shouldValidate: true });
                                    if (picked?.state) {
                                      form.setValue(`addresses.${index}.state` as any, { value: picked.state, label: picked.state });
                                    }
                                  }}
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                >
                                  <option value="">Choose a city</option>
                                  {(cities ?? []).map((c) => (
                                    <option key={c.id} value={c.id}>
                                      {c.name}
                                      {c.state ? `, ${c.state}` : ""}
                                    </option>
                                  ))}
                                </select>
                              )}
                            />
                            <FormField
                              form={form}
                              name={`addresses.${index}.pincode` as any}
                              label="Pincode"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Pincode"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  {form.formState.errors.addresses &&
                    !Array.isArray(form.formState.errors.addresses) && (
                      <p className="text-xs text-destructive mt-1">
                        {form.formState.errors.addresses.message}
                      </p>
                    )}
                </div>

                {/* status change section */}
                {(initialData && showStatusSection) && (
                  <div className="space-y-4 ">
                    <div className="grid grid-cols-2 gap-4">
                      <FormItem>
                        <FormLabel>Change Status</FormLabel>
                        <Select
                          value={statusObj.status}
                          onValueChange={(e) =>
                            setStatusObj((prev) => ({
                              ...prev,
                              status: e,
                            }))
                          }
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Ready to deliver">
                              Ready to deliver
                            </SelectItem>
                            {defaultDeliveryMode?.value != "pickup" || form.getValues("deliveryMode.value") !== "pickup" && (
                              <SelectItem value="Out for delivery">
                                Out for delivery
                              </SelectItem>
                            )}
                         
                            <SelectItem value="Delivered">Delivered</SelectItem>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage className="text-red-700" />
                      </FormItem>

                      <FormItem>
                        <FormLabel htmlFor={"date"}>Enter the Date</FormLabel>
                        <FormControl>
                          <input
                            type="date"
                         
                            value={defaultDeliveryDate || statusObj?.date || ""}
                            onChange={(e) =>
                              setStatusObj((prev) => ({
                                ...prev,
                                date: e.target.value,
                              }))
                            }
                            className="border border-gray-300 rounded-md p-2 w-full"
                          />
                        </FormControl>
                        <FormMessage className="text-red-700" />
                      </FormItem>
                      {defaultDeliveryMode?.value != "pickup" && form.getValues("deliveryMode.value") !== "pickup" && (
                        <FormItem>
                          <FormLabel>Assign to</FormLabel>

                          <Select
                            value={statusObj.assign_to}
                            onValueChange={(value) =>
                              setStatusObj((prev) => ({
                                ...prev,
                                assign_to: value,
                              }))
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select delivery person" />
                              </SelectTrigger>
                            </FormControl>

                            <SelectContent>
                              {relationShipManagerOptions?.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={String(option.value)}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <FormMessage className="text-red-700" />
                        </FormItem>

                      )}
                    </div>
                  </div>
                )}
           
              </div>
            
              <AlertDialogFooter className="h-min flex items-center gap-2 flex-row py-5 px-10">
                <Button
                  className="text-blue-700 border border-blue-700 max-w-[80px] w-full"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    form.reset(defaultDelivery);
                    onClose();
                  }}
                >
                  Cancel
                </Button>
                <AlertDialogAction asChild>
                <Button
                  className="border border-blue-700 text-white-A700 max-w-[80px] w-full bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="submit"
                  onClick={() => console.log("🟡 UPDATE BUTTON CLICKED")}
                  disabled={
                    initialData &&
                    statusObj?.status === "Ready to deliver" &&
                    !statusObj?.date
                  }

                >
                  {initialData ? "Update" : "Save"}
                  </Button>
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </FormProvider>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}